import { resolveUrl } from '@/lib/api/core'

/**
 * Resolves an expired Supabase signed URL to the stable /api/attachments/resolve
 * endpoint so the browser never tries to load a provably-broken URL.
 *
 * Used by both MarkdownPreview (ReactMarkdown img renderer) and TipTap's
 * custom Image NodeView so both render paths handle expired tokens consistently.
 */
const SIGN_MARKER = '/storage/v1/object/sign/flownotes-assets/'

/**
 * Full URL for markdown/TipTap image src. Relative /api/... paths must use
 * VITE_API_BASE_URL on production (SPA host has no API; e.g. flownotes.space).
 */
export function resolveMarkdownImageSrc(src: string): string {
  const resolved = resolveSignedUrlIfExpired(src || '')
  if (!resolved) return ''
  if (resolved.startsWith('/api/')) {
    return resolveUrl(resolved)
  }
  return resolved
}

export function resolveSignedUrlIfExpired(src: string): string {
  if (!src || !src.includes(SIGN_MARKER)) return src
  try {
    const tokenMatch = src.match(/[?&]token=([^&]+)/)
    if (tokenMatch) {
      const payload = JSON.parse(atob(tokenMatch[1].split('.')[1]))
      const nowSec = Math.floor(Date.now() / 1000)
      if (payload.exp && payload.exp < nowSec) {
        const afterBucket = src.split(SIGN_MARKER)[1]
        const storagePath = afterBucket ? afterBucket.split('?')[0] : null
        if (storagePath) {
          return `/api/attachments/resolve?path=${encodeURIComponent(storagePath)}`
        }
      }
    }
  } catch {
    // Malformed token � use original src
  }
  return src
}
